import { AlertTriangle, CheckCircle2, RotateCw, ScanLine, WifiOff } from 'lucide-react'
import { Button } from '@/ui/Button'
import type { ScanRecord } from '@/lib/api-types'

export interface SubmissionOutcome {
  delivered: boolean
  scan: ScanRecord | null
  /** Human-readable reason. Always present when `delivered` is false. */
  reason: string | null
  /** False when the scan was never stored — nothing to retry against. */
  stored: boolean
  offline: boolean
}

interface Props {
  outcome: SubmissionOutcome
  retrying: boolean
  onRetry: () => void
  onDismiss: () => void
  onScanAgain: () => void
}

/**
 * The result of a submission, stated plainly.
 *
 * This screen is the "fail loudly" decision made concrete. There is no
 * background queue: a scan either reached n8n or it did not, and if it did not
 * the operator is told immediately, in words, with the code still on screen so
 * they can act on it.
 *
 * The failure state is deliberately full-screen and requires a deliberate tap
 * to clear. A toast that fades after three seconds is exactly how a failed scan
 * gets missed in a workshop.
 */
export function ScanResult({ outcome, retrying, onRetry, onDismiss, onScanAgain }: Props) {
  const { delivered, scan, reason, stored, offline } = outcome

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="scan-result-title"
      className="fixed inset-0 z-[95] flex flex-col justify-end bg-slate-900/70 backdrop-blur-sm"
    >
      <div className="pb-safe max-h-[88dvh] overflow-y-auto rounded-t-[var(--radius-panel)] bg-[var(--surface-raised)] p-6">
        <div className="flex items-start gap-4">
          <span
            className={
              delivered
                ? 'grid h-14 w-14 shrink-0 place-items-center rounded-full bg-ok-50 text-ok-600'
                : 'grid h-14 w-14 shrink-0 place-items-center rounded-full bg-bad-50 text-bad-600'
            }
          >
            {delivered ? (
              <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
            ) : offline ? (
              <WifiOff className="h-8 w-8" aria-hidden="true" />
            ) : (
              <AlertTriangle className="h-8 w-8" aria-hidden="true" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <h2 id="scan-result-title" className="text-xl font-bold tracking-tight">
              {delivered ? 'Sent' : offline ? 'Not sent — no connection' : 'Not sent'}
            </h2>
            <p className="mt-1 text-[0.9375rem] leading-relaxed text-[var(--text-secondary)]">
              {delivered
                ? `${scan?.workflowName ?? 'The workflow'} received this scan.`
                : (reason ?? 'The scan could not be delivered.')}
            </p>
          </div>
        </div>

        {scan !== null && (
          <div className="mt-5 space-y-3">
            <div className="rounded-2xl bg-[var(--surface-sunken)] p-4">
              <p className="text-sm font-semibold text-[var(--text-muted)]">Code</p>
              <p className="mt-1 break-all font-mono text-[0.9375rem]">{scan.code}</p>
            </div>

            {Object.keys(scan.formData).length > 0 && (
              <dl className="divide-y divide-[var(--border-subtle)] overflow-hidden rounded-2xl bg-[var(--surface-sunken)]">
                {Object.entries(scan.formData).map(([key, value]) => (
                  <div key={key} className="flex items-baseline justify-between gap-4 p-4">
                    <dt className="text-sm text-[var(--text-muted)]">{key}</dt>
                    <dd className="text-right text-[0.9375rem] font-medium">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}

        {!delivered && stored && (
          // Says the useful thing: the scan is not lost, and it can be re-sent
          // later from History if now is not a good moment.
          <p className="mt-4 rounded-2xl bg-warn-50 p-3 text-sm text-warn-700">
            This scan is saved and shows as failed in History. You can retry it here or from there.
          </p>
        )}

        {!delivered && !stored && (
          <p className="mt-4 rounded-2xl bg-bad-50 p-3 text-sm text-bad-700">
            Nothing was saved, so this scan needs taking again.
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {delivered ? (
            <Button size="lg" fullWidth leadingIcon={<ScanLine className="h-5 w-5" />} onClick={onScanAgain}>
              Scan next
            </Button>
          ) : (
            <>
              {stored && (
                <Button
                  size="lg"
                  fullWidth
                  busy={retrying}
                  leadingIcon={<RotateCw className="h-5 w-5" aria-hidden="true" />}
                  onClick={onRetry}
                >
                  Retry now
                </Button>
              )}
              <Button variant="secondary" size="lg" fullWidth onClick={onScanAgain}>
                {stored ? 'Carry on scanning' : 'Scan again'}
              </Button>
            </>
          )}
          <Button variant="ghost" fullWidth onClick={onDismiss}>
            Back to workflows
          </Button>
        </div>
      </div>
    </div>
  )
}
